import React, { useState } from 'react';
import DataBreachForm from './DataBreachForm';
import RansomwareForm from './RansomwareForm';
import CyberAttackForm from './CyberAttackForm';
import SystemBreachForm from './SystemBreachForm';
import EmailBreachForm from './EmailBreachForm';
import DeviceBreachForm from './DeviceBreachForm';

const ItBreachForm = ({ setStep, onSubmit, formType }) => {
  // Route to the appropriate form based on formType
  switch (formType) {
    case 'dataBreachForm':
      return <DataBreachForm setStep={setStep} onSubmit={onSubmit} />;
    case 'ransomwareForm':
      return <RansomwareForm setStep={setStep} onSubmit={onSubmit} />;
    case 'cyberAttackForm':
      return <CyberAttackForm setStep={setStep} onSubmit={onSubmit} />;
    case 'systemBreachForm':
      return <SystemBreachForm setStep={setStep} onSubmit={onSubmit} />;
    case 'emailBreachForm':
      return <EmailBreachForm setStep={setStep} onSubmit={onSubmit} />;
    case 'deviceBreachForm':
      return <DeviceBreachForm setStep={setStep} onSubmit={onSubmit} />;
    default:
      // For now, show a placeholder for forms not yet created
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Form Under Development</h2>
          <p>The {formType} form is being developed.</p>
          <button onClick={() => setStep('itBreachMenu')}>Back to Menu</button>
        </div>
      );
  }
};

// Keep the original comprehensive form as a fallback (currently unused)
// eslint-disable-next-line no-unused-vars
const ComprehensiveItBreachForm = ({ setStep, onSubmit, formType }) => {
  const [formData, setFormData] = useState({
    incidentType: formType || '',
    dateTimeDiscovered: '',
    dateTimeOccurred: '',
    discoveredBy: '',
    affectedSystems: '',
    affectedPatientCount: '',
    dataTypes: [],
    incidentDescription: '',
    immediateActions: '',
    containmentMeasures: '',
    notificationsMade: [],
    lawEnforcementContacted: false,
    vendorInvolved: '',
    estimatedCost: '',
    preventiveMeasures: '',
    followUpRequired: false,
    followUpDetails: '',
    reportedBy: '',
    reportedByTitle: '',
    reportedByEmail: '',
    reportedByPhone: ''
  });

  const getFormTitle = () => {
    const titles = {
      'dataBreachForm': 'Data Breach Report',
      'ransomwareForm': 'Ransomware Attack Report',
      'cyberAttackForm': 'Cyber Attack Report',
      'systemBreachForm': 'System Breach Report',
      'emailBreachForm': 'Email Compromise Report',
      'deviceBreachForm': 'Device Theft/Loss Report'
    };
    return titles[formType] || 'IT Security Incident Report';
  };

  const dataTypeOptions = [
    'Patient Names',
    'Social Security Numbers',
    'Medical Record Numbers',
    'Health Insurance Information',
    'Medical History',
    'Treatment Information',
    'Billing Information',
    'Email Addresses',
    'Phone Numbers',
    'Addresses',
    'Financial Information',
    'Other PHI'
  ];

  const notificationOptions = [
    'Practice Management',
    'IT Department',
    'Legal Counsel',
    'Insurance Provider',
    'Patients',
    'HHS/OCR',
    'State Authorities',
    'Media',
    'Business Associates'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMultiSelectChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked 
        ? [...prev[fieldName], value]
        : prev[fieldName].filter(item => item !== value)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      formType: 'ITBreach',
      subType: formType,
      submittedAt: new Date()
    });
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <h1>{getFormTitle()}</h1>
        <p>Complete all relevant fields for this security incident</p>
      </div>

      <form onSubmit={handleSubmit} className="incident-form">
        {/* Basic Incident Information */}
        <div className="form-section">
          <h3>Incident Details</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date/Time Discovered *</label>
              <input
                type="datetime-local"
                name="dateTimeDiscovered"
                value={formData.dateTimeDiscovered}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Date/Time Occurred (if known)</label>
              <input
                type="datetime-local"
                name="dateTimeOccurred"
                value={formData.dateTimeOccurred}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Discovered By *</label>
              <input
                type="text"
                name="discoveredBy"
                value={formData.discoveredBy}
                onChange={handleInputChange}
                placeholder="Name and title of person who discovered the incident"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Affected Systems/Applications *</label>
            <textarea
              name="affectedSystems"
              value={formData.affectedSystems}
              onChange={handleInputChange}
              placeholder="List all systems, applications, or devices affected"
              rows="3"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Estimated Number of Affected Patients</label>
              <input
                type="number"
                name="affectedPatientCount"
                value={formData.affectedPatientCount}
                onChange={handleInputChange}
                placeholder="Enter number or 'Unknown'"
              />
            </div>
          </div>
        </div>

        {/* Data Types Affected */}
        <div className="form-section">
          <h3>Data Types Potentially Compromised</h3>
          <div className="checkbox-grid">
            {dataTypeOptions.map(option => (
              <label key={option} className="checkbox-label">
                <input
                  type="checkbox"
                  value={option}
                  checked={formData.dataTypes.includes(option)}
                  onChange={(e) => handleMultiSelectChange(e, 'dataTypes')}
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        {/* Incident Description */}
        <div className="form-section">
          <h3>Incident Description</h3>
          
          <div className="form-group">
            <label>Detailed Description of Incident *</label>
            <textarea
              name="incidentDescription"
              value={formData.incidentDescription}
              onChange={handleInputChange}
              placeholder="Provide a detailed description of what happened, how it was discovered, and the timeline of events"
              rows="5"
              required
            />
          </div>

          <div className="form-group">
            <label>Immediate Actions Taken</label>
            <textarea
              name="immediateActions"
              value={formData.immediateActions}
              onChange={handleInputChange}
              placeholder="Describe any immediate steps taken to address the incident"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Containment Measures Implemented</label>
            <textarea
              name="containmentMeasures"
              value={formData.containmentMeasures}
              onChange={handleInputChange}
              placeholder="Describe measures taken to contain or stop the incident"
              rows="3"
            />
          </div>
        </div>

        {/* Notifications and Response */}
        <div className="form-section">
          <h3>Notifications and Response</h3>
          
          <div className="form-group">
            <label>Notifications Made</label>
            <div className="checkbox-grid">
              {notificationOptions.map(option => (
                <label key={option} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={option}
                    checked={formData.notificationsMade.includes(option)}
                    onChange={(e) => handleMultiSelectChange(e, 'notificationsMade')}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="lawEnforcementContacted"
                  checked={formData.lawEnforcementContacted}
                  onChange={handleInputChange}
                />
                Law Enforcement Contacted
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Vendor/Third Party Involved</label>
              <input
                type="text"
                name="vendorInvolved"
                value={formData.vendorInvolved}
                onChange={handleInputChange}
                placeholder="Name of any vendors or third parties involved"
              />
            </div>
            <div className="form-group">
              <label>Estimated Cost of Incident</label>
              <input
                type="text"
                name="estimatedCost"
                value={formData.estimatedCost}
                onChange={handleInputChange}
                placeholder="Estimated financial impact"
              />
            </div>
          </div>
        </div>

        {/* Follow-up and Prevention */}
        <div className="form-section">
          <h3>Prevention and Follow-up</h3>
          
          <div className="form-group">
            <label>Preventive Measures Recommended</label>
            <textarea
              name="preventiveMeasures"
              value={formData.preventiveMeasures}
              onChange={handleInputChange}
              placeholder="Describe measures to prevent similar incidents in the future"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="followUpRequired"
                checked={formData.followUpRequired}
                onChange={handleInputChange}
              />
              Follow-up Required
            </label>
          </div>

          {formData.followUpRequired && (
            <div className="form-group">
              <label>Follow-up Details</label>
              <textarea
                name="followUpDetails"
                value={formData.followUpDetails}
                onChange={handleInputChange}
                placeholder="Describe required follow-up actions and timeline"
                rows="3"
              />
            </div>
          )}
        </div>

        {/* Reporter Information */}
        <div className="form-section">
          <h3>Reporter Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Reported By *</label>
              <input
                type="text"
                name="reportedBy"
                value={formData.reportedBy}
                onChange={handleInputChange}
                placeholder="Your full name"
                required
              />
            </div>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="reportedByTitle"
                value={formData.reportedByTitle}
                onChange={handleInputChange}
                placeholder="Your job title"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="reportedByEmail"
                value={formData.reportedByEmail}
                onChange={handleInputChange}
                placeholder="Your email address"
                required
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="reportedByPhone"
                value={formData.reportedByPhone}
                onChange={handleInputChange}
                placeholder="Your phone number"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="submit" className="submit-button">
            Submit Report
          </button>
        </div>
      </form>
    </div>
  );
};

export default ItBreachForm;
